/**
 * Map Wrapper
 *
 * The Map Wrapper module is mainly responsible for rendering a map.
 * This module contains the following features:
 *
 * * Load styles:
 *   Will load the styles passed in the options.theme when rendering the map.
 *   If there are traffic flow options, the styles loader module will also load them, displaying both on the map.
 *
 * * Copyrights:
 *   We make use of the TomTomAttributionControl module to request the copyrights by using our services.
 *   We then display them on the map.
 *
 * * Request URL and Analytics:
 *   In order to use the TomTom URL and the necessary headers to request the map tiles, we transform the request
 *   (transformRequestFactory) to obtain the correct URL and set the headers with the cors headers used in tomtom.
 *
 */
import mapboxGlRTLText from 'raw-loader!mapbox-gl-rtl-text';
import initializeMapOptions from './initializeMapOptions';
import initializeCopyrights from './initializeCopyrights';
import transformRequestFactory from './transformRequestFactory';
import {mapboxMapFactory, appendMapboxProperties} from 'Map/map/mapFactory';
import {isNil} from 'lodash';
import converters from 'Services/services/converters';
import validators from 'Services/services/validators';
import tiersContainer from 'Map/tier/tiersContainer';
import mapTiersFactory from 'Map/configurer/mapTiersFactory';
import promise from 'Map/utils/promiseFactory';
import extendedMapFactory from 'Map/map/extendedMap';
import sourcesValidation from './validateUsageOfTomtomSources';
import {debounce} from 'lodash';

const INITIAL_STYLE = {
    'version': 8,
    'sources': {},
    'layers': []
};

function initializeStyle({sprite, glyphs}) {
    const style = JSON.parse(JSON.stringify(INITIAL_STYLE));
    style.sprite = sprite;
    style.glyphs = glyphs;
    return style;
}

function convertAndValidateLanguage(languageOption) {
    return converters.language(languageOption, 'maps');
}

const geopoliticalViewValidator = validators.oneOfValue(['Unified', 'IL', 'IN', 'MA', 'PK', 'AR', 'Arabic'],
    'geopoliticalView');

function convertAndValidateGeopoliticalView(geopoliticalViewOption) {
    if (isNil(geopoliticalViewOption) || geopoliticalViewOption === '') {
        return geopoliticalViewOption;
    }

    geopoliticalViewValidator(geopoliticalViewOption);
    return geopoliticalViewOption;
}

/**
 * @private
 * @class MapConfigurer
 * @constructor
 * @param {Object} options
 * @param {Object} dependencies
 * @param {CoreMap} dependencies.coreMap
 */
export default class MapConfigurer {
    constructor(options, {
        mapbox
    }) {
        this.options = {...options};

        this.options = initializeMapOptions(options);

        this.options.attributionControl = false;
        this.mapbox = mapbox;

        this.options.language = convertAndValidateLanguage(this.options.language);
        this.options.geopoliticalView = convertAndValidateGeopoliticalView(this.options.geopoliticalView);

        this.tiersContainer = tiersContainer();
        this.options.transformRequest = transformRequestFactory(this.tiersContainer);
    }

    /**
     * @method createMap
     * @return {Map} Instance of the map
     */
    createMap = () => {
        const style = initializeStyle(this.options);
        const map = mapboxMapFactory({style, ...this.options});
        const extendedMap = extendedMapFactory(this.tiersContainer, this.options);
        appendMapboxProperties(extendedMap, map);
        const proxiedMap = extendedMap;
        initializeCopyrights(this.options.key, this.options.attributionControlPosition, proxiedMap);
        Promise.all([
            promise((resolve) => {
                proxiedMap.once('load', () => {
                    const tiers = mapTiersFactory(this.options);
                    resolve(Promise.all(tiers.map(tier => proxiedMap.addTier(tier))));
                });
            }),
            (async () => {
                const blob = new Blob([mapboxGlRTLText], {type: 'text/javascript'});

                // a similar check is done internally when setting the plugin
                // but it's not exposed outside the method
                // eslint-disable-next-line
                // https://github.com/mapbox/mapbox-gl-js/blob/e0c4d4679d06be7c0f51970b26a184f8d9ed6d5b/src/source/rtl_text_plugin.js
                if (this.mapbox.RTLTextPluginRequested === true) {
                    return;
                }
                this.mapbox.RTLTextPluginRequested = true;

                this.mapbox
                    .setRTLTextPlugin(URL.createObjectURL(blob),
                        (error) => {
                            throw Error(error);
                        });
            })()
        ])
            .then(() => {
                window.setTimeout(() => {
                    sourcesValidation.tomtomSourcesValidatorHandler(proxiedMap);
                    proxiedMap.on('styledata', debounce(
                        () => {
                            sourcesValidation.tomtomSourcesValidatorHandler(proxiedMap);
                        },
                        100,
                        {
                            leading: true,
                            trailing: false
                        }
                    ));
                }, 2000);
            })
            .catch(reason => {
                console.error(reason);
            });

        return proxiedMap;
    }
}



// WEBPACK FOOTER //
// ./map/configurer/mapConfigurer.js